import { useCallback, useEffect, useState } from "react";
import { Receipt, FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, apiError, fmtDuration, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";

const kr = (n) => `${Number(n || 0).toFixed(2)} kr`;

export const InvoicePanel = ({ jobId }) => {
  const { t } = useTranslation();
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/invoice`);
      setInv(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  const openPdf = () => {
    const a = document.createElement("a");
    a.href = `${process.env.REACT_APP_BACKEND_URL}/api/jobs/${jobId}/invoice/pdf?auth=${getToken()}`;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (!inv) return null;
  const s = inv.summary;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-white p-4" data-testid="invoice-panel">
      <div className="flex flex-wrap items-center gap-2">
        <h2 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
          <Receipt className="h-4 w-4 text-slate-400" /> {t("invoice.title")}
        </h2>
        <Button
          size="sm"
          variant="outline"
          data-testid="invoice-refresh"
          onClick={load}
          disabled={loading}
          className="ml-auto h-9 bg-white transition-colors duration-200 hover:bg-slate-100"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> {t("common.refresh")}
        </Button>
        <Button
          size="sm"
          data-testid="invoice-pdf-button"
          onClick={openPdf}
          className="h-9 bg-blue-600 text-white transition-colors duration-200 hover:bg-blue-700"
        >
          <FileDown className="mr-1.5 h-3.5 w-3.5" /> PDF
        </Button>
      </div>

      <div className="mt-3 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-[0.05em] text-slate-500">
            <tr>
              <th className="px-2 py-2">{t("invoice.row")}</th>
              <th className="px-2 py-2 text-right">{t("common.qty")}</th>
              <th className="px-2 py-2 text-right">{t("invoice.unitPrice")}</th>
              <th className="px-2 py-2 text-right">{t("common.sum")}</th>
            </tr>
          </thead>
          <tbody>
            {inv.lines.map((l) => (
              <tr key={l.label} data-testid={`invoice-line-${l.label}`} className="border-t border-slate-100">
                <td className="px-2 py-2 font-medium text-slate-900">{l.label}</td>
                <td className="px-2 py-2 text-right text-slate-600">{l.qty} {l.unit}</td>
                <td className="px-2 py-2 text-right text-slate-600">{kr(l.price)}</td>
                <td className="px-2 py-2 text-right font-medium">{kr(l.sum)}</td>
              </tr>
            ))}
            {[...inv.materials.map((m) => ({ ...m, tag: t("invoice.material") })),
              ...inv.spare_parts.map((m) => ({ ...m, tag: t("invoice.sparePart") })),
              ...(inv.others || []).map((m) => ({ ...m, tag: t("invoice.other") }))].map((m, i) => (
              <tr key={`${m.tag}-${i}`} data-testid="invoice-item-row" className="border-t border-slate-100">
                <td className="px-2 py-2 text-slate-900">{m.tag}: {m.name}</td>
                <td className="px-2 py-2 text-right text-slate-600">{m.qty} st</td>
                <td className="px-2 py-2 text-right text-slate-600">{kr(m.price)}</td>
                <td className="px-2 py-2 text-right font-medium">{kr(m.sum)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 rounded-md bg-slate-900 p-3 text-white">
        <div className="flex items-center justify-between py-1 text-sm">
          <span className="text-slate-300">{t("invoice.workAndTravel", { work: fmtDuration(s.work_seconds), travel: fmtDuration(s.travel_seconds) })}</span>
          <span data-testid="invoice-labour">{kr(s.work_amount + s.travel_amount)}</span>
        </div>
        <div className="flex items-center justify-between py-1 text-sm">
          <span className="text-slate-300">{t("invoice.materialsAll")}</span>
          <span data-testid="invoice-materials">{kr(s.material_amount + s.parts_amount + (s.other_amount || 0))}</span>
        </div>
        {inv.distance?.total_mil ? (
          <div className="flex items-center justify-between py-1 text-sm">
            <span className="text-slate-300">
              {t("invoice.distanceLine", { mil: inv.distance.total_mil, rate: inv.rates.mil_rate })}
            </span>
            <span data-testid="invoice-mil">{kr(s.mil_amount)}</span>
          </div>
        ) : null}
        <div className="flex items-center justify-between border-t border-slate-700 py-1 pt-2 text-sm">
          <span className="text-slate-300">{t("invoice.net")}</span>
          <span data-testid="invoice-net">{kr(s.net)}</span>
        </div>
        <div className="flex items-center justify-between py-1 text-sm">
          <span className="text-slate-300">{t("invoice.vat", { percent: s.vat_percent })}</span>
          <span data-testid="invoice-vat">{kr(s.vat)}</span>
        </div>
        <div className="flex items-center justify-between pt-1">
          <span className="text-xs font-medium uppercase tracking-[0.05em] text-slate-300">{t("invoice.toPay")}</span>
          <span className="font-heading text-lg font-bold" data-testid="invoice-total">{kr(s.total)}</span>
        </div>
      </div>
    </div>
  );
};

export default InvoicePanel;
